import axios from 'axios';
import config from '../config/environment';

// Get backend configuration from environment
const BACKEND_URL = config.BACKEND_URL;
const API_BASE_URL = config.API_BASE_URL;

// Enhanced logging based on environment configuration
if (config.ENABLE_API_LOGGING || config.IS_DEVELOPMENT) {
  console.log('🔧 API Service Configuration:');
  console.log(`Backend URL: ${BACKEND_URL}`);
  console.log(`API Base URL: ${API_BASE_URL}`);
  console.log(`Environment: ${config.ENVIRONMENT}`);
  console.log(`Debug Logs: ${config.ENABLE_DEBUG_LOGS ? 'ON' : 'OFF'}`);
}

// Create axios instance with environment-based config
const instance = axios.create({
  baseURL: BACKEND_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  },
  timeout: config.IS_PRODUCTION ? 30000 : 15000, // Longer timeout for production
  withCredentials: false
});

// Add request interceptor to attach the auth token
instance.interceptors.request.use(
  config_req => {
    const token = localStorage.getItem('token');
    if (token) {
      config_req.headers['Authorization'] = `Bearer ${token}`;
    }
    
    // Enhanced request logging
    if (config.ENABLE_API_LOGGING && config.IS_DEVELOPMENT) {
      console.log(`🚀 API Request: ${config_req.method?.toUpperCase()} ${config_req.url}`);
      if (config_req.data) {
        console.log('📤 Request Data:', config_req.data);
      }
    }
    
    return config_req;
  },
  error => {
    if (config.ENABLE_API_LOGGING) {
      console.error('❌ Request Error:', error);
    }
    return Promise.reject(error);
  }
);

// Add response interceptor for error handling
instance.interceptors.response.use(
  response => {
    // Enhanced response logging
    if (config.ENABLE_API_LOGGING && config.IS_DEVELOPMENT) {
      console.log(`✅ API Response: ${response.status} ${response.config.method?.toUpperCase()} ${response.config.url}`);
    }
    return response;
  },
  error => {
    // Enhanced error logging
    const errorInfo = {
      message: error.message,
      status: error.response?.status,
      statusText: error.response?.statusText,
      url: error.config?.url,
      method: error.config?.method?.toUpperCase()
    };
    
    if (config.ENABLE_API_LOGGING || config.IS_DEVELOPMENT) {
      console.error('❌ API Error:', errorInfo);
      if (error.response?.data) {
        console.error('📥 Error Data:', error.response.data);
      }
    }
    
    // Handle specific error cases
    if (error.response?.status === 401) {
      console.warn('🔐 Authentication failed - redirecting to login');
      localStorage.removeItem('token');
      // You can add redirect logic here if needed
    }
    
    return Promise.reject(error);
  }
);

// Modules API - Using environment-based URL construction
export const modulesAPI = {
  getAllModules: () => instance.get(config.getApiUrl('/modules')),
  getModuleById: (id) => instance.get(config.getApiUrl(`/modules/${id}`)),
  addModule: (moduleData) => instance.post(config.getApiUrl('/modules'), moduleData),
  updateModule: (id, moduleData) => instance.put(config.getApiUrl(`/modules/${id}`), moduleData),
  deleteModule: (id) => instance.delete(config.getApiUrl(`/modules/${id}`))
};

// Staff API - Using environment-based URL construction
export const staffAPI = {
  getAllStaff: () => instance.get(config.getApiUrl('/staff')),
  getStaffById: (id) => instance.get(config.getApiUrl(`/staff/${id}`)),
  addStaff: (staffData) => instance.post(config.getApiUrl('/staff'), staffData),
  updateStaff: (id, staffData) => instance.put(config.getApiUrl(`/staff/${id}`), staffData),
  deleteStaff: (id) => instance.delete(config.getApiUrl(`/staff/${id}`)),
  
  // Lecturer profile management
  getProfile: () => instance.get(config.getApiUrl('/staff/profile')),
  updateProfile: (profileData) => instance.put(config.getApiUrl('/staff/profile'), profileData)
};

// Students API - Environment-based URLs with enhanced error handling
export const studentsAPI = {
  getAllStudents: async (filters = {}) => {
    try {
      console.log('Original filters:', filters);
      
      // Clean up filters before sending
      const cleanFilters = {};
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '' && value !== 'all') {
          cleanFilters[key] = value;
        }
      });

      console.log('Cleaned filters:', cleanFilters);
      
      const response = await instance.get(config.getApiUrl('/students'), {
        params: cleanFilters
      });

      console.log('API Response:', response.data);
      return { data: response.data };
    } catch (error) {
      console.error('Failed to fetch students:', error);
      throw error;
    }
  },
  getStudentById: async (id) => {
    try {
      console.log('=== FETCHING STUDENT BY ID ===');
      console.log('Student ID:', id);
      
      // Validate ID
      if (!id) {
        throw new Error('Student ID is required');
      }
      
      // Check authentication
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }
      
      console.log('Making request to:', config.getApiUrl(`/students/${id}`));
      console.log('Token exists:', !!token);
      
      const response = await instance.get(config.getApiUrl(`/students/${id}`));
      console.log('Student fetch response:', response.data);
      
      return { data: response.data };
    } catch (error) {
      console.error('Error fetching student by ID:', error);
      console.error('Error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message
      });
      throw error;
    }
  },
  addStudent: (studentData) => instance.post(config.getApiUrl('/students'), studentData),
  createStudent: (studentData) => instance.post(config.getApiUrl('/students'), studentData),
  updateStudent: (id, studentData) => instance.put(config.getApiUrl(`/students/${id}`), studentData),
  deleteStudent: (id) => instance.delete(config.getApiUrl(`/students/${id}`)),
  
  // Student profile management (authenticated student only)
  getProfile: () => {
    console.log('API: Getting student profile from', config.getApiUrl('/students/profile'));
    return instance.get(config.getApiUrl('/students/profile'));
  },
  updateProfile: (profileData) => {
    console.log('API: Updating student profile with data:', profileData);
    console.log('API: Making PUT request to', config.getApiUrl('/students/profile'));
    return instance.put(config.getApiUrl('/students/profile'), profileData);
  },
  getAttendanceRecords: () => instance.get(config.getApiUrl('/students/attendance-records'))
};

// Lecturers API - Environment-based URLs
export const lecturersAPI = {
  getAllLecturers: async (filters = {}) => {
    try {
      console.log('=== API SERVICE DEBUG ===');
      console.log('Making request to', config.getApiUrl('/staff'));
      console.log('Original filters:', filters);
      
      const cleanFilters = {};
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '' && value !== 'all') {
          cleanFilters[key] = value;
        }
      });

      console.log('Cleaned filters:', cleanFilters);
      console.log('Request URL:', config.getApiUrl('/staff'));
      
      const token = localStorage.getItem('token');
      console.log('Auth token exists:', !!token);
      console.log('Token preview:', token ? `${token.substring(0, 20)}...` : 'No token');
      
      const response = await instance.get(config.getApiUrl('/staff'), {
        params: cleanFilters
      });

      console.log('Raw API Response:', response);
      console.log('Response status:', response.status);
      console.log('Response data:', response.data);
      
      return response;
    } catch (error) {
      console.error('API Error Details:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        config: {
          url: error.config?.url,
          method: error.config?.method,
          baseURL: error.config?.baseURL
        }
      });
      
      if (error.response?.status === 401) {
        console.error('Authentication failed - token may be expired');
        localStorage.removeItem('token');
      }
      
      throw error;
    }
  },
  getLecturerById: (id) => instance.get(config.getApiUrl(`/staff/${id}`)),
  addLecturer: (lecturerData) => instance.post(config.getApiUrl('/staff'), lecturerData),
  updateLecturer: (id, lecturerData) => instance.put(config.getApiUrl(`/staff/${id}`), lecturerData),
  deleteLecturer: (id) => instance.delete(config.getApiUrl(`/staff/${id}`))
};

// Groups API - Environment-based URLs
export const groupsAPI = {
  getAllGroups: () => instance.get(config.getApiUrl('/groups')),
  getGroupsCount: () => instance.get(config.getApiUrl('/groups/count')),
  getGroupById: (id) => instance.get(config.getApiUrl(`/groups/${id}`)),
  addGroup: (groupData) => instance.post(config.getApiUrl('/groups'), groupData),
  updateGroup: (id, groupData) => instance.put(config.getApiUrl(`/groups/${id}`), groupData),
  deleteGroup: (id) => instance.delete(config.getApiUrl(`/groups/${id}`))
};

// Degrees API - Environment-based URLs
export const degreesAPI = {
  getAllDegrees: () => instance.get(config.getApiUrl('/degrees')),
  getDegreeById: (id) => instance.get(config.getApiUrl(`/degrees/${id}`)),
  addDegree: (degreeData) => instance.post(config.getApiUrl('/degrees'), degreeData),
  updateDegree: (id, degreeData) => instance.put(config.getApiUrl(`/degrees/${id}`), degreeData),
  getFilteredDegrees: (params) => instance.get(config.getApiUrl('/degrees/filtered'), { params })
};

// Filtered data API for cascading dropdowns - Environment-based URLs
export const filtersAPI = {
  getFilteredGroups: (params) => instance.get(config.getApiUrl('/groups/filtered'), { params }),
  getFilteredModules: (params) => instance.get(config.getApiUrl('/modules/filtered'), { params }),
  getFilteredStudents: (params) => instance.get(config.getApiUrl('/students/filtered'), { params })
};

// Settings API - Environment-based URLs
export const settingsAPI = {
  getProfile: () => instance.get(config.getApiUrl('/settings/profile')),
  updateProfile: (profileData) => instance.put(config.getApiUrl('/settings/profile'), profileData)
};

// Attendance API - Environment-based URLs
export const attendanceAPI = {
  getAllAttendance: () => instance.get(config.getApiUrl('/attendance')),
  getAttendanceById: (id) => instance.get(config.getApiUrl(`/attendance/${id}`)),
  addAttendance: (attendanceData) => instance.post(config.getApiUrl('/attendance'), attendanceData),
  updateAttendance: (id, attendanceData) => instance.put(config.getApiUrl(`/attendance/${id}`), attendanceData),
  deleteAttendance: (id) => instance.delete(config.getApiUrl(`/attendance/${id}`)),
  getStudentModuleAttendanceReport: (params) => instance.get(config.getApiUrl('/attendance/reports/student-module-attendance'), { params }),
  getStudentAttendanceCount: (params) => instance.get(config.getApiUrl('/attendance/reports/student-attendance-count'), { params }),
  getAttendanceSummary: () => instance.get(config.getApiUrl('/attendance/reports/summary')),
  getLecturerStats: () => instance.get(config.getApiUrl('/attendance/lecturer-stats')),
  getRecentAttendance: () => instance.get(config.getApiUrl('/attendance/recent')),
  getUniversityStats: () => instance.get(config.getApiUrl('/attendance/university-stats')),
  getUniversityRecentAttendance: () => instance.get(config.getApiUrl('/attendance/university-recent'))
};

// Export the default axios instance for auth and general requests
export default instance;

// Export configuration constants for use in other files
export { BACKEND_URL, API_BASE_URL, config as environmentConfig };

// Health check function using environment variables
export const healthCheck = () => instance.get(config.getApiUrl('/ping'));

// Utility function to test API connectivity
export const testConnection = async () => {
  try {
    const response = await healthCheck();
    console.log('✅ API Connection Test Successful:', response.data);
    return { success: true, data: response.data };
  } catch (error) {
    console.error('❌ API Connection Test Failed:', error);
    return { 
      success: false, 
      error: error.message,
      details: {
        backend_url: BACKEND_URL,
        api_base_url: API_BASE_URL,
        environment: config.ENVIRONMENT
      }
    };
  }
};
